import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import HelpOutline from '@mui/icons-material/HelpOutline';

import { ACTOR_TYPE, EVENT_TYPE, type BookingChange } from '@a1/shared';

import DataGridWithDynamicXScroll from 'components-ts/data-grids/DataGridWithDynamicXScroll';
import { useGetEntityName } from 'hooks-ts/useGetEntity';
import callAzureFunction from 'utils-ts/call-azure-function';
import handleError from 'utils-ts/handle-error';
import { type PaginatedResponse } from 'types/api';

interface ActivityLogsDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  bookingId: string;
}

interface NamedRecord {
  name: string | null;
}

/** A row of `GET bookings/{bookingId}/event-logs`, with the json columns already parsed. */
interface BookingEventLog {
  id: number;
  eventType: EVENT_TYPE;
  occurredAt: string;
  actorType: ACTOR_TYPE;
  actorLabel: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  metadata: {
    manager?: { firstName: string | null; lastName: string | null } | null;
    service?: NamedRecord | null;
    location?: NamedRecord | null;
    customStatus?: NamedRecord | null;
  };
  preChange: BookingChange | null;
  postChange: BookingChange | null;
}

interface EntityNames {
  booking: string;
  manager: string;
  externalUser: string;
}

const formatTime = (hours?: number | null, minutes?: number | null) =>
  dayjs()
    .hour(hours ?? 0)
    .minute(minutes ?? 0)
    .format('hh:mm a');

const describeSchedule = ({ date, startHours, startMinutes, endHours, endMinutes }: BookingChange = {}) =>
  `${date ? `${dayjs(date).format('DD/MM/YYYY')} ` : ''}from ${formatTime(startHours, startMinutes)} to ${formatTime(endHours, endMinutes)}`;

const describeEvent = (event: BookingEventLog, { booking, manager }: EntityNames): string => {
  const { eventType, metadata, fromStatus, toStatus, preChange, postChange } = event;

  switch (eventType) {
    case EVENT_TYPE.BOOKING_STATUS_CHANGED:
      return `The status has been changed from ${fromStatus} to ${toStatus}`;
    case EVENT_TYPE.BOOKING_CUSTOM_STATUS_CHANGED:
      return metadata.customStatus?.name
        ? `The custom status has been changed to ${metadata.customStatus.name}`
        : 'The custom status has been cleared';
    case EVENT_TYPE.BOOKING_MANAGER_CHANGED:
      return metadata.manager
        ? `The ${booking} has been re-assigned to another ${manager} - ${metadata.manager.firstName} ${metadata.manager.lastName}`
        : `The ${booking} is no longer assigned to a ${manager}`;
    case EVENT_TYPE.BOOKING_ANY_MANAGER_AT_LOCATION_CHANGED:
      return `The ${booking} has been re-assigned to any available ${manager} at location`;
    case EVENT_TYPE.BOOKING_SERVICE_CHANGED:
      return `The ${booking} service has been updated into ${metadata.service?.name ?? 'no service'}`;
    case EVENT_TYPE.BOOKING_LOCATION_CHANGED:
      return `The ${booking} location has been changed to ${metadata.location?.name ?? 'no location'}`;
    case EVENT_TYPE.BOOKING_RESCHEDULED:
      return `The ${booking} is rescheduled to ${describeSchedule(postChange ?? {})}, from ${describeSchedule(preChange ?? {})}`;
    case EVENT_TYPE.BOOKING_REMINDER_SENT:
      return `A ${booking} confirmation has been sent`;
    default:
      return eventType;
  }
};

const getActorTypeLabel = (actorType: ACTOR_TYPE, { manager, externalUser }: EntityNames): string => {
  switch (actorType) {
    case ACTOR_TYPE.MANAGER:
      return manager;
    case ACTOR_TYPE.EXTERNAL_USER:
      return externalUser;
    case ACTOR_TYPE.ADMIN:
      return 'Admin';
    case ACTOR_TYPE.SYSTEM:
      return 'System';
    default:
      return actorType;
  }
};

const ActivityLogsDialog = ({ open, setOpen, bookingId }: ActivityLogsDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<BookingEventLog[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 10, page: 0 });

  const entityNames: EntityNames = {
    booking: useGetEntityName('booking').toLowerCase(),
    manager: useGetEntityName('manager').toLowerCase(),
    externalUser: useGetEntityName('externalUser').toLowerCase()
  };

  const getActivityLogs = useCallback(async () => {
    try {
      setLoading(true);
      const { page, pageSize } = paginationModel;
      const response = await callAzureFunction<PaginatedResponse<BookingEventLog[]>>({
        url: `bookings/${bookingId}/event-logs`,
        method: 'get',
        params: { page, pageSize }
      });

      setRowCount(response.data.count ?? 0);
      setEvents(response.data.rows ?? []);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [bookingId, paginationModel]);

  useEffect(() => {
    if (open) getActivityLogs();
  }, [getActivityLogs, open]);

  const columns: GridColDef<BookingEventLog>[] = [
    { field: 'id', headerName: 'Log ID', sortable: false, width: 100 },
    {
      field: 'activity',
      headerName: 'Activity',
      sortable: false,
      flex: 1,
      minWidth: 300,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ whiteSpace: 'normal', py: 1 }}>
          {describeEvent(row, entityNames)}
        </Typography>
      )
    },
    {
      field: 'actorLabel',
      headerName: 'Performed By',
      sortable: false,
      minWidth: 150,
      valueGetter: (_, row) => row.actorLabel ?? '-'
    },
    {
      field: 'actorType',
      headerName: 'User Type',
      sortable: false,
      minWidth: 200,
      valueGetter: (_, row) => getActorTypeLabel(row.actorType, entityNames)
    },
    {
      field: 'occurredAt',
      headerName: 'Time of Activity',
      sortable: false,
      minWidth: 200,
      valueGetter: (_, row) => dayjs(row.occurredAt).format('YYYY-MM-DD hh:mm:ss a')
    }
  ];

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl" fullWidth>
      <DialogTitle variant="h3" display="flex" alignItems="center" gap={1}>
        Activity Logs{' '}
        <Tooltip
          title={`Changes made to this ${entityNames.booking}, and confirmations sent for it, are logged and displayed here.`}
          placement="right"
        >
          <HelpOutline sx={{ cursor: 'pointer' }} />
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <DataGridWithDynamicXScroll
          getRowHeight={() => 'auto'}
          disableColumnMenu
          loading={loading}
          rows={events}
          columns={columns}
          density="comfortable"
          disableRowSelectionOnClick
          disableColumnFilter
          autoHeight
          rowCount={rowCount}
          paginationModel={paginationModel}
          paginationMode="server"
          localeText={{ noRowsLabel: 'No activity logs found' }}
          onPaginationModelChange={setPaginationModel}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ActivityLogsDialog;
